import { useState } from "react";
import { Box, Flex, Text, Button, Input, Stack, HStack, Badge, Icon, IconButton } from "@chakra-ui/react";
import { Plus, Trash2, ChevronUp, ChevronDown, ShieldCheck, Building2, Check } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { listIdsInSchema } from "@/lib/templateFields";
import { inputStyle } from "@/pages/auth/AuthShell";
import { colors } from "@/theme/colors";

const CheckToggle = ({ checked, onChange, label, disabled, testId }) => (
  <HStack as="label" gap="2" cursor={disabled ? "default" : "pointer"} opacity={disabled ? 0.6 : 1}>
    <Box as="input" type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
      data-testid={testId} accentColor={colors.teal} w="15px" h="15px" />
    <Text fontSize="sm" color={colors.textPrimary}>{label}</Text>
  </HStack>
);

// Coordinator: owns selection state; sidebar handles create/pick, detail handles editing.
export function ListManager({ isSuperAdmin, activeCompanyId, companyName, lists, templates, reload, flash }) {
  const [selectedId, setSelectedId] = useState(null);

  const masters = lists.filter((l) => l.company_id === null);
  const mine = isSuperAdmin ? [] : lists.filter((l) => l.company_id === activeCompanyId);
  const selected = lists.find((l) => l.id === selectedId) || masters[0] || mine[0] || null;

  const canEdit = selected && (
    (selected.company_id === null && isSuperAdmin) ||
    (selected.company_id !== null && selected.company_id === activeCompanyId)
  );

  const usedIn = selected
    ? templates.filter((t) => listIdsInSchema(t.schema).has(selected.id))
    : [];

  return (
    <Flex gap="4" align="flex-start" direction={{ base: "column", md: "row" }} data-testid="list-manager">
      <ListSidebar isSuperAdmin={isSuperAdmin} activeCompanyId={activeCompanyId} companyName={companyName}
        masters={masters} mine={mine} selected={selected} onSelect={setSelectedId} reload={reload} flash={flash} />
      <ListDetail selected={selected} canEdit={canEdit} usedIn={usedIn}
        onDeleted={() => setSelectedId(null)} reload={reload} flash={flash} />
    </Flex>
  );
}

function ListSidebar({ isSuperAdmin, activeCompanyId, companyName, masters, mine, selected, onSelect, reload, flash }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const createList = async () => {
    if (!newName.trim()) return;
    const { data, error } = await supabase.from("select_lists")
      .insert({ company_id: isSuperAdmin ? null : activeCompanyId, name: newName.trim(), items: [], include_other: false })
      .select().single();
    if (error) { flash("error", error.message); return; }
    setNewName("");
    setCreating(false);
    onSelect(data.id);
    flash("success", `List “${data.name}” created. Add its items below.`);
    reload();
  };

  const ListLink = ({ l }) => {
    const active = selected?.id === l.id;
    return (
      <Box as="button" onClick={() => onSelect(l.id)} data-testid={`list-link-${l.id}`}
        display="block" w="full" textAlign="left" px="3" py="2" borderRadius="8px"
        bg={active ? colors.tealTint : "transparent"} _hover={{ bg: active ? colors.tealTint : colors.cream }}>
        <Text fontSize="sm" fontWeight={active ? "600" : "500"} color={active ? colors.tealHover : colors.textPrimary}>{l.name}</Text>
      </Box>
    );
  };

  return (
    <Box w={{ base: "full", md: "260px" }} flexShrink={0} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="3">
      <Button size="sm" w="full" onClick={() => setCreating(true)} data-testid="add-list-btn" mb="3">
        <Plus size={15} /> Add list
      </Button>
      {creating && (
        <HStack gap="1.5" mb="3">
          <Input {...inputStyle} size="sm" autoFocus placeholder="List name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createList()} data-testid="new-list-name-input" />
          <IconButton aria-label="Create list" size="sm" bg={colors.teal} color="white" _hover={{ bg: colors.tealHover }}
            onClick={createList} data-testid="confirm-new-list-btn"><Check size={15} /></IconButton>
        </HStack>
      )}
      <HStack gap="1.5" px="1" mb="1">
        <Icon as={ShieldCheck} boxSize="3.5" color={colors.teal} />
        <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color={colors.textSecondary}>Hakina lists</Text>
      </HStack>
      <Stack gap="0.5" mb={mine.length || !isSuperAdmin ? "3" : "0"}>
        {masters.map((l) => <ListLink key={l.id} l={l} />)}
        {masters.length === 0 && <Text px="3" py="1" fontSize="xs" color={colors.textSecondary}>None yet.</Text>}
      </Stack>
      {!isSuperAdmin && (
        <>
          <HStack gap="1.5" px="1" mb="1">
            <Icon as={Building2} boxSize="3.5" color={colors.sageDeep} />
            <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color={colors.textSecondary}>{companyName} lists</Text>
          </HStack>
          <Stack gap="0.5">
            {mine.map((l) => <ListLink key={l.id} l={l} />)}
            {mine.length === 0 && <Text px="3" py="1" fontSize="xs" color={colors.textSecondary}>No company lists yet.</Text>}
          </Stack>
        </>
      )}
    </Box>
  );
}

function ListDetail({ selected, canEdit, usedIn, onDeleted, reload, flash }) {
  const [savedTick, setSavedTick] = useState(false);

  const persist = async (patch) => {
    const { error } = await supabase.from("select_lists").update(patch).eq("id", selected.id);
    if (error) { flash("error", error.message); return false; }
    setSavedTick(true);
    setTimeout(() => setSavedTick(false), 2000);
    reload();
    return true;
  };

  const deleteList = async () => {
    if (usedIn.length > 0) {
      flash("error", `“${selected.name}” is used in ${usedIn.length} template(s). Update those templates first.`);
      return;
    }
    if (!window.confirm(`Delete list “${selected.name}”?`)) return;
    const { error } = await supabase.from("select_lists").delete().eq("id", selected.id);
    if (error) flash("error", error.message);
    else { flash("success", "List deleted."); onDeleted(); reload(); }
  };

  return (
    <Box flex="1" w="full" bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4">
      {!selected ? (
        <Text fontSize="sm" color={colors.textSecondary} data-testid="list-detail-empty">Create a list to get started. Lists power your dropdown fields in templates.</Text>
      ) : (
        <Stack gap="4" data-testid="list-detail">
          <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
            <Box flex="1" minW="200px">
              {canEdit ? (
                <Input {...inputStyle} fontWeight="700" fontSize="md" defaultValue={selected.name} key={selected.id}
                  onBlur={(e) => e.target.value.trim() && e.target.value !== selected.name && persist({ name: e.target.value.trim() })}
                  data-testid="list-name-input" />
              ) : (
                <HStack gap="2">
                  <Text fontWeight="700" color={colors.textPrimary}>{selected.name}</Text>
                  <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5">Managed by Hakina</Badge>
                </HStack>
              )}
              <Text fontSize="xs" color={colors.textSecondary} mt="1" data-testid="list-usage">
                Currently used in {usedIn.length} template{usedIn.length === 1 ? "" : "s"}
                {usedIn.length > 0 && <>: {usedIn.map((t) => t.title).join(", ")}</>}
              </Text>
            </Box>
            <HStack gap="2">
              {savedTick && <Badge bg={colors.resolvedBg} color={colors.resolvedText} borderRadius="full" px="2.5" data-testid="list-saved-badge">List saved</Badge>}
              {canEdit && (
                <Button size="xs" variant="outline" color={colors.poppy} onClick={deleteList} data-testid="delete-list-btn">
                  <Trash2 size={14} /> Delete list
                </Button>
              )}
            </HStack>
          </Flex>

          <ListItemsEditor selected={selected} canEdit={canEdit} onItems={(items) => persist({ items })} />

          {canEdit && (
            <CheckToggle checked={selected.include_other} onChange={(e) => persist({ include_other: e.target.checked })}
              label={`Include “Other” option`} testId="list-include-other" />
          )}
        </Stack>
      )}
    </Box>
  );
}

function ListItemsEditor({ selected, canEdit, onItems }) {
  const move = (i, dir) => {
    const items = [...selected.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    onItems(items);
  };

  return (
    <>
      <Stack gap="1.5">
        {selected.items.map((item, i) => (
          <HStack key={`${selected.id}-${i}-${item}`} gap="2">
            <Box w="6px" h="6px" borderRadius="full" bg={colors.teal} flexShrink={0} />
            {canEdit ? (
              <Input {...inputStyle} size="sm" defaultValue={item}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== item) onItems(selected.items.map((x, j) => (j === i ? v : x)));
                }}
                data-testid={`list-item-input-${i}`} />
            ) : (
              <Text fontSize="sm" color={colors.textPrimary} flex="1">{item}</Text>
            )}
            {canEdit && (
              <HStack gap="0">
                <IconButton aria-label="Move up" size="xs" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`list-item-up-${i}`}><ChevronUp size={14} /></IconButton>
                <IconButton aria-label="Move down" size="xs" variant="ghost" disabled={i === selected.items.length - 1} onClick={() => move(i, 1)} data-testid={`list-item-down-${i}`}><ChevronDown size={14} /></IconButton>
                <IconButton aria-label="Remove item" size="xs" variant="ghost" color={colors.poppy}
                  onClick={() => onItems(selected.items.filter((_, j) => j !== i))} data-testid={`list-item-remove-${i}`}><Trash2 size={14} /></IconButton>
              </HStack>
            )}
          </HStack>
        ))}
        {selected.items.length === 0 && <Text fontSize="sm" color={colors.textSecondary}>No items yet.</Text>}
      </Stack>
      {canEdit && <AddItemRow onAdd={(v) => onItems([...selected.items, v])} />}
    </>
  );
}

function AddItemRow({ onAdd }) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");
  const confirm = () => {
    if (!value.trim()) return;
    onAdd(value.trim());
    setValue("");
    setAdding(false);
  };
  if (!adding) {
    return (
      <Button size="xs" alignSelf="flex-start" onClick={() => setAdding(true)} data-testid="add-list-item-btn">
        <Plus size={14} /> Add item
      </Button>
    );
  }
  return (
    <HStack gap="1.5" maxW="360px">
      <Input {...inputStyle} size="sm" autoFocus placeholder="New item" value={value}
        onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirm()}
        data-testid="new-list-item-input" />
      <IconButton aria-label="Confirm item" size="sm" bg={colors.teal} color="white" _hover={{ bg: colors.tealHover }}
        onClick={confirm} data-testid="confirm-list-item-btn"><Check size={15} /></IconButton>
    </HStack>
  );
}
